import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form'

import { Box, Link, Text } from '@factorialco/deprecated-design-system'

import Selector from 'modules/core/components/Selector'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { PayrollConceptWithCategory } from './CategorizedPayrollConceptUtil'

type SelectorPopoverProps = Omit<React.ComponentProps<typeof Selector.Popover>, 'children'>

type PayrollConceptSelectProps = {
  payrollConcepts: PayrollConceptWithCategory[]
} & Omit<SelectorPopoverProps, 'placeholder' | 'blankLabel'>

function PayrollConceptSelect({ payrollConcepts, ...selectorProps }: PayrollConceptSelectProps) {
  const { navigation } = useNavigation()

  return (
    <Selector.Popover
      {...selectorProps}
      mandatory
      placeholder={i18n.t('common.select')}
      blankLabel={i18n.t('common.empty')}
      footer={
        <Box
          padding="s24"
          width="full"
          border={{ all: { color: 'viridian100', style: 'solid', width: 's1' } }}
          borderRadius={{ all: 'abs004' }}
          background="viridian100"
          justifyContent="spaceAround"
        >
          <Text.Normal size="300" weight="semibold">
            {i18n.t(
              'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.callout_title'
            )}
          </Text.Normal>
          <Box flexDirection="row" gap="s8" paddingTop="s8">
            <Text.Normal>
              {i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.go_to'
              )}{' '}
              <Link.Href
                href={navigation.settings.compensations.payrollConcepts.fullPath()}
                target="_blank"
                rel="noopener noreferrer"
              >
                {i18n.t(
                  'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.payroll_concepts_settings_link'
                )}
              </Link.Href>{' '}
              {i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.to_create_enable_or_edit_them'
              )}
            </Text.Normal>
          </Box>
        </Box>
      }
    >
      {payrollConcepts.map((option) => (
        <Selector.Option
          key={option.value}
          value={option.value}
          label={option.label}
          searchValue={option.label}
        >
          <Box
            width="full"
            flexDirection="row"
            justifyContent="spaceBetween"
            overflowY="hidden"
            position="relative"
          >
            <Text.Normal weight="medium" ellipsis>
              {option.label}
            </Text.Normal>
          </Box>
        </Selector.Option>
      ))}
    </Selector.Popover>
  )
}

type PayrollConceptSelectFieldProps<TFormValues extends FieldValues> = {
  payrollConcepts: PayrollConceptWithCategory[]
  name: Path<TFormValues>
} & Omit<SelectorPopoverProps, 'value' | 'onChange' | 'error'>

function PayrollConceptSelectField<TFormValues extends FieldValues>({
  payrollConcepts,
  name,
  ...selectorPopOverProps
}: PayrollConceptSelectFieldProps<TFormValues>) {
  const payrollConceptSelectField = useFormContext<TFormValues>()
  const { control, getValues } = payrollConceptSelectField
  const value = getValues(name)

  return (
    <Controller<TFormValues>
      name={name}
      control={control}
      rules={{ required: true }}
      render={({ field, formState: { errors } }) => {
        return (
          <PayrollConceptSelect
            {...field}
            {...selectorPopOverProps}
            payrollConcepts={payrollConcepts}
            value={value ? [value] : []}
            error={
              errors?.['payrollConcept']?.message
                ? String(errors['payrollConcept'].message)
                : undefined
            }
          />
        )
      }}
    />
  )
}

export { PayrollConceptSelectField, PayrollConceptSelect }
export type { PayrollConceptSelectFieldProps }
